import type { MacroInputSelfTestSample } from './inputSelfTestRecipe';

export const MACRO_INPUT_SOAK_TEST_DURATION_US = 15 * 60 * 1_000_000;
export const MACRO_INPUT_SOAK_SEGMENT_US = 30 * 1_000_000;
export const MACRO_INPUT_SOAK_SAMPLE_INTERVAL_US = 1_500_000;
export const MACRO_INPUT_SOAK_MAX_SAMPLES = 10_000;

export interface MacroInputSoakTestRecipe {
  seed: number;
  durationUs: number;
  samples: readonly MacroInputSelfTestSample[];
}

type ActionKind = 'key' | 'mouse-motion' | 'mouse-button' | 'mouse-wheel' | 'touch'
  | 'gamepad0' | 'gamepad1' | 'gamepad2' | 'gamepad3';

const actionKinds: readonly ActionKind[] = ['key', 'mouse-motion', 'mouse-button', 'mouse-wheel', 'touch',
  'gamepad0', 'gamepad1', 'gamepad2', 'gamepad3'];
const keys = [
  { vk: 65, scan: 30, extended: false }, { vk: 66, scan: 48, extended: false },
  { vk: 67, scan: 46, extended: false }, { vk: 68, scan: 32, extended: false },
  { vk: 87, scan: 17, extended: false }, { vk: 88, scan: 45, extended: false },
  { vk: 89, scan: 21, extended: false }, { vk: 90, scan: 44, extended: false },
  { vk: 37, scan: 75, extended: true }, { vk: 38, scan: 72, extended: true },
  { vk: 39, scan: 77, extended: true }, { vk: 40, scan: 80, extended: true },
] as const;
const buttons = ['left', 'right', 'middle', 'back', 'forward'] as const;
const gamepadMasks = [0x0001, 0x0002, 0x0004, 0x0008, 0x0010, 0x0020, 0x0040, 0x0080,
  0x0100, 0x0200, 0x1000, 0x2000, 0x4000, 0x8000] as const;

/** Deterministic, balanced 15-minute workload. Every action releases its owned state within one slot. */
export function createMacroInputSoakTestRecipe(seed: number): MacroInputSoakTestRecipe {
  const normalizedSeed = seed >>> 0, random = new XorShift32(normalizedSeed);
  const samples: MacroInputSelfTestSample[] = [];
  const state: GeneratorState = { cursor: { x: .5, y: .5 } };
  const segmentCount = MACRO_INPUT_SOAK_TEST_DURATION_US / MACRO_INPUT_SOAK_SEGMENT_US;
  for (let segment = 0; segment < segmentCount; segment++) {
    const coverage = segment === 0
      ? ['mouse-motion' as const, ...random.shuffle(actionKinds.filter(kind => kind !== 'mouse-motion'))]
      : random.shuffle([...actionKinds]);
    const base = segment * MACRO_INPUT_SOAK_SEGMENT_US;
    const slots = Math.floor((MACRO_INPUT_SOAK_SEGMENT_US - 1_000_000) / MACRO_INPUT_SOAK_SAMPLE_INTERVAL_US);
    for (let action = 0; action < slots; action++) {
      const kind = coverage[action] ?? random.pick(actionKinds);
      const tUs = base + 500_000 + action * MACRO_INPUT_SOAK_SAMPLE_INTERVAL_US;
      appendAction(samples, random, kind, segment, action, tUs, segment % 2 === 0 ? 'absolute' : 'relative', state);
    }
  }
  if (samples.length > MACRO_INPUT_SOAK_MAX_SAMPLES) throw Error('input_soak_recipe_capacity');
  return { seed: normalizedSeed, durationUs: MACRO_INPUT_SOAK_TEST_DURATION_US, samples: Object.freeze(samples) };
}

export function macroInputSoakMouseMode(tUs: number): 'absolute' | 'relative' {
  return Math.floor(tUs / MACRO_INPUT_SOAK_SEGMENT_US) % 2 === 0 ? 'absolute' : 'relative';
}

function appendAction(output: MacroInputSelfTestSample[], random: XorShift32, kind: ActionKind,
  segment: number, action: number, tUs: number, motion: 'absolute' | 'relative', state: GeneratorState): void {
  const id = (suffix: string) => `s${segment}-a${action}-${suffix}`;
  if (kind === 'key') return appendKey(output, random, id, tUs);
  if (kind === 'mouse-motion') {
    if (motion === 'absolute') {
      state.cursor = random.point();
      output.push({ id: id('mouse-abs'), tUs, kind: 'mouse-abs', ...state.cursor });
    } else {
      const dx = random.signed(64), dy = random.signed(64);
      state.cursor = { x: clamp(state.cursor.x + dx / 1279), y: clamp(state.cursor.y + dy / 719) };
      output.push({ id: id('mouse-rel'), tUs, kind: 'mouse-rel', dx, dy });
    }
    return;
  }
  if (kind === 'mouse-button') {
    const button = random.pick(buttons);
    output.push({ id: id(`${button}-down`), tUs, kind: 'mouse-button', button, action: 'down', ...state.cursor },
      { id: id(`${button}-up`), tUs: tUs + 150_000, kind: 'mouse-button', button, action: 'up', ...state.cursor });
    return;
  }
  if (kind === 'mouse-wheel') {
    const delta = random.bool() ? 120 : -120, horizontal = random.bool();
    output.push({ id: id(horizontal ? 'wheel-h' : 'wheel-v'), tUs, kind: 'mouse-wheel',
      dx: horizontal ? delta : 0, dy: horizontal ? 0 : delta });
    return;
  }
  if (kind === 'touch') return appendTouch(output, random, id, tUs);
  appendGamepad(output, random, id, tUs, Number(kind.at(-1)) as 0 | 1 | 2 | 3);
}

function appendKey(output: MacroInputSelfTestSample[], random: XorShift32, id: (suffix: string) => string, tUs: number): void {
  const key = random.pick(keys), chord = random.int(5) === 0;
  if (chord) output.push({ id: id('shift-down'), tUs, kind: 'key', vk: 16, scan: 42, extended: false, action: 'down' });
  const offset = chord ? 150_000 : 0;
  output.push({ id: id(`key-${key.vk}-down`), tUs: tUs + offset, kind: 'key', ...key, action: 'down' },
    { id: id(`key-${key.vk}-up`), tUs: tUs + offset + 150_000, kind: 'key', ...key, action: 'up' });
  if (chord) output.push({ id: id('shift-up'), tUs: tUs + 450_000, kind: 'key', vk: 16, scan: 42, extended: false, action: 'up' });
}

function appendTouch(output: MacroInputSelfTestSample[], random: XorShift32, id: (suffix: string) => string, tUs: number): void {
  const contacts = 1 + random.int(3), starts = Array.from({ length: contacts }, () => random.point());
  const moves = starts.map(point => ({ x: clamp(point.x + random.fraction(-.15, .15)), y: clamp(point.y + random.fraction(-.15, .15)) }));
  starts.forEach((point, contactId) => output.push({ id: id(`touch-${contactId}-down`), tUs: tUs + contactId * 150_000,
    kind: 'touch', contactId, action: 'down', ...point }));
  moves.forEach((point, contactId) => output.push({ id: id(`touch-${contactId}-move`), tUs: tUs + (contacts + contactId) * 150_000,
    kind: 'touch', contactId, action: 'move', ...point }));
  starts.forEach((_point, index) => {
    const contactId = contacts - index - 1;
    output.push({ id: id(`touch-${contactId}-up`), tUs: tUs + (contacts * 2 + index) * 150_000,
      kind: 'touch', contactId, action: 'up', ...moves[contactId] });
  });
}

function appendGamepad(output: MacroInputSelfTestSample[], random: XorShift32, id: (suffix: string) => string,
  tUs: number, slot: 0 | 1 | 2 | 3): void {
  const buttons = random.pick(gamepadMasks) | (random.bool() ? random.pick(gamepadMasks) : 0);
  output.push({ id: id(`gamepad-${slot}-active`), tUs, kind: 'gamepad', slot, buttons,
    lt: random.int(256), rt: random.int(256), lx: random.axis(), ly: random.axis(), rx: random.axis(), ry: random.axis() },
  { id: id(`gamepad-${slot}-neutral`), tUs: tUs + 150_000, kind: 'gamepad', slot,
    buttons: 0, lt: 0, rt: 0, lx: 0, ly: 0, rx: 0, ry: 0 });
}

interface GeneratorState { cursor: { x: number; y: number } }

class XorShift32 {
  private state: number;
  constructor(seed: number) { this.state = (seed || 0x6d2b79f5) >>> 0; }
  next(): number { let value = this.state; value ^= value << 13; value ^= value >>> 17; value ^= value << 5;
    this.state = value >>> 0; return this.state; }
  int(limit: number): number { return Math.floor(this.next() / 0x1_0000_0000 * limit); }
  bool(): boolean { return (this.next() & 1) === 1; }
  pick<T>(values: readonly T[]): T { return values[this.int(values.length)]; }
  shuffle<T>(values: T[]): T[] { for (let index = values.length - 1; index > 0; index--) {
    const swap = this.int(index + 1); [values[index], values[swap]] = [values[swap], values[index]];
  } return values; }
  fraction(minimum: number, maximum: number): number { return minimum + this.next() / 0x1_0000_0000 * (maximum - minimum); }
  signed(maximum: number): number { const value = this.int(maximum * 2 + 1) - maximum; return value || 1; }
  axis(): number { return this.int(65_536) - 32_768; }
  point(): { x: number; y: number } { return { x: clamp(this.fraction(.08, .92)), y: clamp(this.fraction(.08, .92)) }; }
}

function clamp(value: number): number { return Math.round(Math.max(.02, Math.min(.98, value)) * 10_000) / 10_000; }
